import React, { useEffect, useMemo, useState } from "react";
import { Search, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatIDR, formatDate, todayISO, COURT_COUNTS, courtsFor } from "@/lib/format";
import { PageHeader, Card, Button, Field, Pill, TierBadge, Loading } from "@/components/kit";

const ADDON_PRESETS = ["Wasit", "Fotografer", "Coach", "Sewa Raket", "Beli Bola", "Lainnya"];
const toInt = (v) => parseInt(String(v).replace(/[^0-9]/g, ""), 10) || 0;

export default function Record() {
  const { user } = useAuth();
  const [members, setMembers] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [category, setCategory] = useState("Padel");
  const [txnType, setTxnType] = useState("visit");
  const [duration, setDuration] = useState(1);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [method, setMethod] = useState("Transfer");
  const [date, setDate] = useState(todayISO());
  const [memStart, setMemStart] = useState(todayISO());
  const [dp, setDp] = useState(false);
  const [billTotal, setBillTotal] = useState("");
  const [busy, setBusy] = useState(false);
  // court / class scheduling
  const [court, setCourt] = useState("");
  const [schedDate, setSchedDate] = useState(todayISO());
  const [schedTime, setSchedTime] = useState("");
  const [sessions, setSessions] = useState([]);
  const [dateMode, setDateMode] = useState("upcoming");
  const [customDate, setCustomDate] = useState(todayISO());
  const [classSearch, setClassSearch] = useState("");
  const [className, setClassName] = useState("");
  const [addons, setAddons] = useState([]);
  const [dynCats, setDynCats] = useState([]);
  const [classCats, setClassCats] = useState([]);

  const sessionCats = useMemo(() => [...new Set((sessions || []).map((s) => s.category).filter(Boolean))], [sessions]);
  const classCatSet = useMemo(() => new Set([...(classCats || []), ...sessionCats]), [classCats, sessionCats]);
  const categories = useMemo(() => {
    const merged = [...new Set([...(dynCats || []), ...sessionCats])];
    return merged.length ? merged : [category];
  }, [dynCats, sessionCats, category]);

  const isCourt = !!COURT_COUNTS[category];
  const isClass = classCatSet.has(category);
  const addonsTotal = useMemo(() => addons.reduce((s, a) => s + toInt(a.amount), 0), [addons]);
  const grandTotal = (isCourt ? toInt(amount) + addonsTotal : toInt(amount));

  useEffect(() => { api.ownerMembers().then(setMembers).catch(() => setMembers([])); }, []);
  // Server-side member search: finds ANY registered member (name / phone / id), not just the loaded page.
  useEffect(() => {
    let active = true;
    const t = setTimeout(() => {
      api.ownerMembers(q).then((r) => { if (active) setMembers(r); }).catch(() => { if (active) setMembers([]); });
    }, q ? 300 : 0);
    return () => { active = false; clearTimeout(t); };
  }, [q]);
  useEffect(() => { api.classesSummary().then((s) => setSessions(s || [])).catch(() => setSessions([])); }, []);
  useEffect(() => {
    api.listCategories().then((c) => setDynCats(Array.isArray(c) ? c : (c.categories || []))).catch(() => {});
    api.classCategories().then((c) => setClassCats(c || [])).catch(() => {});
  }, []);
  // reset scheduling selection when category changes
  useEffect(() => { setCourt(""); setClassName(""); setSchedTime(""); setAddons([]); }, [category]);

  const filteredMembers = useMemo(() => (members || []).slice(0, 12), [members]);

  const classOptions = useMemo(() => {
    const today = todayISO();
    const needle = classSearch.trim().toLowerCase();
    const catNeedle = String(category || "").trim().toLowerCase();
    return sessions.filter((s) => {
      const catOk = s.category === category || String(s.class_name || "").toLowerCase().includes(catNeedle);
      if (!catOk) return false;
      const d = s.date;
      const dateOk = dateMode === "today" ? d === today
        : dateMode === "upcoming" ? (d >= today)
        : dateMode === "past" ? (d < today)
        : d === customDate;
      if (!dateOk) return false;
      return needle === "" || String(s.class_name).toLowerCase().includes(needle) || String(s.coach || "").toLowerCase().includes(needle);
    }).sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
  }, [sessions, category, dateMode, customDate, classSearch]);

  const pickSession = (s) => {
    setClassName(s.class_name); setSchedDate(s.date); setSchedTime(s.time);
    setNote(`${s.class_name} · ${formatDate(s.date)}`);
  };

  const submit = async () => {
    if (!selected) return toast.error("Pilih member dulu");
    if (!Number(amount)) return toast.error("Isi nominal");
    const cleanAddons = isCourt ? addons.map((a) => ({ label: a.label, amount: toInt(a.amount) })).filter((a) => a.amount > 0) : [];
    const total = isCourt ? toInt(amount) + cleanAddons.reduce((s, a) => s + a.amount, 0) : toInt(amount);
    if (dp && (!Number(billTotal) || Number(billTotal) <= total)) return toast.error("Total tagihan harus lebih besar dari DP (termasuk add-on)");
    if (isCourt && !court) return toast.error("Pilih lapangan/court");
    if (isClass && !className) return toast.error("Pilih sesi kelas");
    setBusy(true);
    try {
      const addonNote = cleanAddons.length ? ` + add-on: ${cleanAddons.map((a) => a.label).join(", ")}` : "";
      const body = {
        category, amount: total, note: (`${note}${addonNote}`).trim() || undefined, txn_type: txnType,
        payment_method: method, txn_date: date,
        ...(txnType === "membership" ? { duration_months: duration, membership_start_date: memStart } : {}),
        ...(isCourt ? { court, schedule_date: schedDate, schedule_time: schedTime || undefined } : {}),
        ...(isClass ? { class_name: className || category, schedule_date: schedDate, schedule_time: schedTime || undefined } : {}),
        ...(cleanAddons.length ? { addons: cleanAddons } : {}),
        ...(dp ? { bill_total: Number(billTotal), payment_type: "DP" } : {}),
      };
      const r = await api.addTransaction(selected.id, body);
      toast.success(`Tercatat ${formatIDR(total)} · +${r.points_earned ?? 0} poin`);
      setAmount(""); setNote(""); setDp(false); setBillTotal(""); setCourt(""); setClassName(""); setSchedTime(""); setAddons([]);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader eyebrow={user.role === "owner" ? "Owner" : "Admin"} title="Catat Transaksi"
        subtitle="Rekam transaksi member. Court, kelas, membership, DP bertahap — tanggal bisnis dapat dipilih manual." />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <div className="mb-3 hsc-label">1 · Pilih Member</div>
          {!members ? <Loading /> : selected ? (
            <div className="flex items-center justify-between rounded-xl border border-[#2C2C2E] bg-[#0E0E10] p-4">
              <div>
                <div className="text-white">{selected.name}</div>
                <div className="mt-1 flex items-center gap-2"><TierBadge tier={selected.tier} /><span className="text-[11px] text-[#6E6E73]">{selected.phone}</span></div>
              </div>
              <Button variant="ghost" onClick={() => setSelected(null)}>Ganti</Button>
            </div>
          ) : (
            <>
              <div className="relative mb-3">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5A5A5F]" />
                <input className="hsc-input pl-9" placeholder="Cari member" value={q} onChange={(e) => setQ(e.target.value)} data-testid="record-member-search" />
              </div>
              <div className="max-h-72 space-y-1 overflow-y-auto">
                {filteredMembers.map((m) => (
                  <button key={m.id} onClick={() => setSelected(m)} data-testid="record-member-option"
                    className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm hover:bg-[#141416]">
                    <span className="text-white">{m.name}</span><span className="text-[11px] text-[#6E6E73]">{m.phone}</span>
                  </button>
                ))}
                {!filteredMembers.length && <div className="py-4 text-center text-sm text-[#6E6E73]">Tidak ada hasil.</div>}
              </div>
            </>
          )}
        </Card>

        <Card>
          <div className="mb-4 hsc-label">2 · Detail Transaksi</div>
          <div className="space-y-4">
            <Field label="Kategori / Lini">
              <div className="flex flex-wrap gap-2" data-testid="record-categories">{categories.map((c) => <Pill key={c} active={category === c} onClick={() => setCategory(c)} data-testid={`record-cat-${c}`}>{c}</Pill>)}</div>
            </Field>

            {isCourt && (
              <div className="space-y-3 rounded-xl border border-[#1F1F22] bg-[#0E0E10] p-3">
                <Field label="Lapangan / Court">
                  <div className="flex flex-wrap gap-2">{courtsFor(category).map((c) => <Pill key={c} active={court === c} onClick={() => setCourt(c)}>{c}</Pill>)}</div>
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Tanggal Main"><input type="date" className="hsc-input" value={schedDate} onChange={(e) => setSchedDate(e.target.value)} data-testid="court-date" /></Field>
                  <Field label="Jam (mis. 18.00-19.00)"><input className="hsc-input" value={schedTime} onChange={(e) => setSchedTime(e.target.value)} /></Field>
                </div>
              </div>
            )}

            {isClass && (
              <div className="space-y-3 rounded-xl border border-[#1F1F22] bg-[#0E0E10] p-3">
                <div className="flex flex-wrap gap-2">
                  {[["today", "Hari Ini"], ["upcoming", "Akan Datang"], ["past", "Lampau"], ["custom", "Tanggal"]].map(([v, l]) => (
                    <Pill key={v} active={dateMode === v} onClick={() => setDateMode(v)} data-testid={`class-datemode-${v}`}>{l}</Pill>
                  ))}
                </div>
                {dateMode === "custom" && <input type="date" className="hsc-input" value={customDate} onChange={(e) => setCustomDate(e.target.value)} />}
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5A5A5F]" />
                  <input className="hsc-input pl-9" placeholder="Cari kelas / coach" value={classSearch} onChange={(e) => setClassSearch(e.target.value)} data-testid="class-search" />
                </div>
                <div className="max-h-56 space-y-2 overflow-y-auto">
                  {classOptions.map((s, i) => {
                    const active = className === s.class_name && schedDate === s.date && schedTime === s.time;
                    return (
                      <button key={i} onClick={() => pickSession(s)} data-testid="class-session-option"
                        className={`flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left transition-colors ${active ? "border-white bg-[#141416]" : "border-[#1F1F22] hover:border-[#6E6E73]"}`}>
                        <div>
                          <div className="text-sm text-white">{s.class_name}</div>
                          <div className="text-[11px] text-[#6E6E73]">{formatDate(s.date)} · {s.time}{s.coach ? ` · ${s.coach}` : ""}</div>
                        </div>
                        <span className="text-[11px] text-[#8E8E93]">{s.enrolled ?? 0}/{s.max ?? "-"}</span>
                      </button>
                    );
                  })}
                  {!classOptions.length && <div className="py-3 text-center text-[12px] text-[#6E6E73]">Tidak ada sesi. Ubah filter tanggal atau catat via jadwal.</div>}
                </div>
              </div>
            )}

            <Field label="Tipe">
              <div className="flex gap-2">
                <Pill active={txnType === "visit"} onClick={() => setTxnType("visit")}>Visit</Pill>
                <Pill active={txnType === "membership"} onClick={() => setTxnType("membership")}>Membership</Pill>
              </div>
            </Field>
            {txnType === "membership" && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Durasi">
                  <div className="flex flex-wrap gap-2">{[1, 2, 3, 6, 12].map((d) => <Pill key={d} active={duration === d} onClick={() => setDuration(d)}>{d}b</Pill>)}</div>
                </Field>
                <Field label="Mulai Membership"><input type="date" className="hsc-input" value={memStart} onChange={(e) => setMemStart(e.target.value)} data-testid="mem-start" /></Field>
              </div>
            )}

            <label className="flex cursor-pointer items-center justify-between rounded-xl border border-[#1F1F22] bg-[#0E0E10] p-3">
              <span className="text-sm text-white">Pembayaran bertahap (DP)</span>
              <button onClick={() => setDp(!dp)} data-testid="dp-toggle" className={`relative h-6 w-11 rounded-full transition-colors ${dp ? "bg-white" : "bg-[#2C2C2E]"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-black transition-transform ${dp ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </label>

            {dp && <Field label="Total Tagihan (Rp)"><input type="number" className="hsc-input" value={billTotal} onChange={(e) => setBillTotal(e.target.value)} data-testid="bill-total" /></Field>}
            <Field label={dp ? "Jumlah DP (Rp)" : "Nominal (Rp)"}><input type="number" className="hsc-input" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="record-amount" /></Field>

            {isCourt && (
              <div className="space-y-2 rounded-xl border border-[#1F1F22] bg-[#0E0E10] p-3" data-testid="addon-section">
                <div className="hsc-label">Add-on (opsional)</div>
                <div className="flex flex-wrap gap-2">
                  {ADDON_PRESETS.map((p) => (
                    <button key={p} type="button" onClick={() => setAddons((prev) => [...prev, { label: p, amount: "" }])}
                      className="flex items-center gap-1 rounded-full border border-[#3A3A3C] px-3 py-1.5 text-xs text-[#D1D1D6] hover:border-white hover:text-white"
                      data-testid={`addon-add-${p}`}>+ {p}</button>
                  ))}
                </div>
                {addons.map((a, idx) => (
                  <div key={`${a.label}-${idx}`} className="flex items-center gap-2" data-testid={`addon-row-${idx}`}>
                    <span className="w-28 shrink-0 text-sm text-white">{a.label}</span>
                    <input type="number" className="hsc-input flex-1" placeholder="Nominal" value={a.amount}
                      onChange={(e) => setAddons((prev) => prev.map((x, i) => (i === idx ? { ...x, amount: e.target.value } : x)))}
                      data-testid={`addon-amount-${idx}`} />
                    <button type="button" onClick={() => setAddons((prev) => prev.filter((_, i) => i !== idx))}
                      className="rounded-lg p-1.5 text-[#FF8080] hover:bg-[#160b0b]" data-testid={`addon-remove-${idx}`}>✕</button>
                  </div>
                ))}
                {grandTotal > 0 && (
                  <div className="mt-1 space-y-1 border-t border-[#1F1F22] pt-2 text-sm">
                    <div className="flex justify-between text-[#8E8E93]"><span>{dp ? "DP" : "Harga Utama"}</span><span>{formatIDR(toInt(amount))}</span></div>
                    {addonsTotal > 0 && <div className="flex justify-between text-[#8E8E93]"><span>Add-ons</span><span>{formatIDR(addonsTotal)}</span></div>}
                    <div className="flex justify-between font-semibold text-white"><span>{dp ? "Total Dibayar" : "Total"}</span><span data-testid="addon-total">{formatIDR(grandTotal)}</span></div>
                  </div>
                )}
              </div>
            )}
            <Field label="Metode Bayar">
              <select className="hsc-input" value={method} onChange={(e) => setMethod(e.target.value)}>{["Transfer", "Cash", "QRIS", "Debit/Credit"].map((m) => <option key={m}>{m}</option>)}</select>
            </Field>
            <Field label="Tanggal Transaksi (revenue date)"><input type="date" className="hsc-input" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} data-testid="txn-date" /></Field>
            <Field label="Catatan (opsional)"><input className="hsc-input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>

            <Button onClick={submit} disabled={busy} className="w-full" data-testid="record-submit">
              <Check className="h-4 w-4" /> Catat {grandTotal ? formatIDR(grandTotal) : "Transaksi"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
